import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import ToggleFilter from '../ToggleFilter';
import TreeView from '@widget/list/TreeView/TreeView';
/**
 * @export
 * The fetchMyMedia options for a tree selection
 */
export function mediaSelectionQuery(sel) {
    if (sel.kind === 'root')
        return { folder: 'root' };
    if (sel.kind === 'folder')
        return { folder: sel.key };
    if (sel.kind === 'tag')
        return { tag: sel.key };
    return { shared: true };
}
/**
 * @export
 * The folder uploads go to (the user's default, else their Private folder), as a selection
 */
export function defaultMediaSelection(folders) {
    const own = folders.filter(f => String(f.IsOwner) === '1');
    const f = own.find(x => String(x.IsDefault) === '1') || own.find(x => x.Kind === 'private');
    return f ? { kind: 'folder', key: String(f.Key) } : null;
}
const isMine = (x) => String(x === null || x === void 0 ? void 0 : x.IsOwner) === '1';
const isPrivate = (f) => (f === null || f === void 0 ? void 0 : f.Kind) === 'private';
const isDefault = (f) => String(f === null || f === void 0 ? void 0 : f.IsDefault) === '1';
/** Folders go at most this many levels deep (the server checks it too) */
const MAX_DEPTH = 3;
/**
 * @export
 * The media library's folders and tags (My Media, and the picker's Library tab) in two
 * sections: "My media" (my folders; Private first) and "Shared with me" (what others share,
 * by owner). Add, edit and delete show on my own folders and tags when the host handles them;
 * the Private folder can't be changed.
 */
const MediaLibraryTree = (props) => {
    const { mode, onModeChange, folders, tags, selection, onSelect, loading, onAddFolder, onEditFolder, onDeleteFolder, onSetDefaultFolder, onEditTag, onDeleteTag } = props;
    const context = useUXPContext();
    const node = (id, label, icon, extra = {}) => (Object.assign({ id, label, icon, path: id, enableAddChildren: false, enableEditNode: false, enableDeleteNode: false }, extra));
    const count = (n) => Number(n || 0) > 0 ? ` (${Number(n)})` : '';
    // Others' items by owner: the owner on top, their shared folders / tags under them
    const byOwner = (list, children) => {
        const owners = Array.from(new Set(list.map(x => x.OwnerName || '')));
        return owners.map(o => node(`o:${o}`, o || '—', 'fal user', { expandOnLoad: true, getChildren: children(list.filter(x => (x.OwnerName || '') === o)) }));
    };
    const sections = React.useMemo(() => {
        if (mode === 'tags') {
            const tagNode = (t, own) => node(`t:${t.Key}`, `${t.Name}${count(t.ItemCount)}`, 'fal tag', {
                original: t, enableEditNode: own && !!onEditTag, enableDeleteNode: own && !!onDeleteTag,
            });
            return {
                mine: tags.filter(isMine).map(t => tagNode(t, true)),
                shared: byOwner(tags.filter(t => !isMine(t)), list => list.map(t => tagNode(t, false))),
            };
        }
        const tree = (list, parent, own) => list
            .filter(f => (f.ParentKey ? String(f.ParentKey) : null) === parent)
            .map(f => node(`f:${f.Key}`, `${f.Name}${isDefault(f) && own ? ` · ${context.$L('uxp-core.media-library.default')}` : ''}${count(f.ItemCount)}`, isPrivate(f) ? 'fal lock' : 'fal folder', {
            original: f,
            enableAddChildren: own && !isPrivate(f) && Number(f.Depth || 1) < MAX_DEPTH && !!onAddFolder,
            enableEditNode: own && !isPrivate(f) && !!onEditFolder,
            enableDeleteNode: own && !isPrivate(f) && !!onDeleteFolder,
            getChildren: tree(list, String(f.Key), own),
        }));
        const shared = folders.filter(f => !isMine(f));
        return {
            mine: tree(folders.filter(isMine), null, true),
            shared: byOwner(shared.filter(f => !f.ParentKey), tops => tops.map(t => tree(shared, null, false).find(n => n.id === `f:${t.Key}`)).filter(Boolean)),
        };
    }, [mode, folders, tags, context, onAddFolder, onEditFolder, onDeleteFolder, onEditTag, onDeleteTag]);
    const selectedId = selection.kind === 'folder' ? `f:${selection.key}` : selection.kind === 'tag' ? `t:${selection.key}` : '';
    const selected = { id: selectedId, label: '', path: selectedId };
    function handleSelect(sel) {
        const n = Array.isArray(sel) ? sel[0] : sel;
        if (!n)
            return;
        if (n.id.startsWith('f:'))
            onSelect({ kind: 'folder', key: n.id.slice(2) });
        else if (n.id.startsWith('t:'))
            onSelect({ kind: 'tag', key: n.id.slice(2) });
    }
    const original = (n) => n === null || n === void 0 ? void 0 : n.original;
    const folderActions = (n) => {
        const f = original(n);
        if (!onSetDefaultFolder || !f || !isMine(f) || isDefault(f))
            return [];
        return [{ icon: 'fal star', tooltip: context.$L('uxp-core.media-library.set-default'), onClick: () => onSetDefaultFolder(f) }];
    };
    const section = (title, items, own, empty) => _jsxs("div", { className: "uxpcore_medialibrarytree__section", children: [_jsx("div", { className: "uxpcore_medialibrarytree__section-title", children: title }), items.length || (own && mode === 'folders' && onAddFolder)
                ? _jsx(TreeView, { items: items, loading: loading, selected: selected, onSelect: handleSelect, enableAdd: own && mode === 'folders' && !!onAddFolder, addButtonLabel: context.$L('uxp-core.media-library.new-folder'), onAdd: (parent) => { var _a; return onAddFolder === null || onAddFolder === void 0 ? void 0 : onAddFolder(((_a = parent === null || parent === void 0 ? void 0 : parent.id) === null || _a === void 0 ? void 0 : _a.startsWith('f:')) ? parent.id.slice(2) : null); }, onEdit: (n) => mode === 'tags' ? onEditTag === null || onEditTag === void 0 ? void 0 : onEditTag(original(n)) : onEditFolder === null || onEditFolder === void 0 ? void 0 : onEditFolder(original(n)), onDelete: (id) => {
                        if (mode === 'tags')
                            onDeleteTag === null || onDeleteTag === void 0 ? void 0 : onDeleteTag(tags.find(t => `t:${t.Key}` === id));
                        else
                            onDeleteFolder === null || onDeleteFolder === void 0 ? void 0 : onDeleteFolder(folders.find(f => `f:${f.Key}` === id));
                    }, customActions: own && mode === 'folders' ? folderActions : undefined, actionButtonsPosition: "inline", expandAllNodes: true })
                : _jsx("div", { className: "uxpcore_medialibrarytree__empty", children: empty })] });
    return _jsxs("div", { className: "uxpcore_medialibrarytree", children: [_jsx("div", { className: "uxpcore_medialibrarytree__toggle", children: _jsx(ToggleFilter, { fill: true, value: mode, onChange: (v) => onModeChange(v), options: [
                        { label: context.$L('uxp-core.media-library.folders'), value: 'folders', icon: 'fal folder' },
                        { label: context.$L('uxp-core.media-library.tags'), value: 'tags', icon: 'fal tag' },
                    ] }) }), _jsxs("div", { className: "uxpcore_medialibrarytree__tree", children: [section(context.$L(mode === 'tags' ? 'uxp-core.media-library.my-tags' : 'uxp-core.media-library.my-media'), sections.mine, true, context.$L(mode === 'tags' ? 'uxp-core.media-library.no-tags' : 'uxp-core.media-library.no-folders')), section(context.$L('uxp-core.media-library.shared-with-me'), sections.shared, false, context.$L('uxp-core.media-library.nothing-shared'))] })] });
};
export default MediaLibraryTree;
